import { Injectable, inject } from '@angular/core';
import { Observable, of, Subject, debounceTime, switchMap, catchError, tap, map, timer } from 'rxjs';
import { AppStateService } from './app-state.service';
import { ApiService } from './api.service';
import { Category, ProductInfo, ProductType, SubCategory } from '../models';

/**
 * Result of {@link ProductsService.getOptionsResult}. Distinguishes a real
 * HTTP failure (`ok: false`) from a legitimately empty panel (`ok: true` with
 * a null/empty `panel`) — the distinction `getOptions()` throws away by
 * collapsing every failure into `null`.
 */
export interface OptionsLoadResult {
  /** `true` when the HTTP call succeeded (the panel may still be null/empty). */
  ok: boolean;
  /** Normalized panel payload when `ok`; `null` on failure or empty response. */
  panel: any;
  /** The underlying error when `ok === false`. */
  error?: unknown;
}

/**
 * Products Service - converted from ProductsDelegate.as
 * 
 * AMFphp Service: ProductsService
 * Methods:
 * - getCategories(locale, user)
 * - getAlbuns(locale, user)
 * - getAlbumEnabledOptions(coverSubtypeId, albumId, user)
 * - getCovers(coverSubtypeId, albumId, locale, user)
 * - checkDefaultCover(albumId, coverId, user)
 * - getProducts(typeId, formType, locale, user, fillFininshOptions)
 * - getStoreProducts(typeId, formType, locale, user, fillFininshOptions)
 * - getOptions(typeId, formType, productId, locale, user)
 * - getProductFinishesOptions(productId, formType, locale, user)
 * - getUniqueFinishes(typeId, finishOptionType, locale, user)
 * - getSameFinishesProducts(typeId, finishes, user)
 * - getProductPrice(orderItemInfo, user)
 * - getProductPack(orderItemsGroup, user)
 * - search(text, brand, locale, user)
 */
@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  private readonly appState = inject(AppStateService);
  private readonly api = inject(ApiService);

  private searchSubject = new Subject<{ text: string; brand: string }>();

  constructor() {
    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(400),
      switchMap(({ text, brand }) => this.performSearch(text, brand))
    ).subscribe(results => {
      this.appState.setSearchResults(results);
    });
  }

  /**
   * Get categories
   */
  getCategories(brand?: string): Observable<Category[]> {
    return this.api.get<any>('/products/categories').pipe(
      map(res => {
        // Handle various response formats: array, { items: [] }, or object with data
        if (Array.isArray(res)) return res;
        if (res?.items) return res.items;
        if (res?.categories) return res.categories;
        console.warn('[ProductsService] Unexpected categories response format:', res);
        return [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Get albums panel
   */
  getAlbunsPanel(): Observable<any[]> {
    return this.api.get<any>('/products/albums').pipe(
      map(res => {
        // REST API returns { albumSubCategories, albumProducts, coversSubCategories }
        // but processAlbumPanelResponse expects an array of arrays (old AMF format)
        if (res && !Array.isArray(res)) {
          const result: any[] = [];
          if (Array.isArray(res.coversSubCategories) && res.coversSubCategories.length > 0) {
            result.push(res.coversSubCategories);
          }
          if (Array.isArray(res.albumSubCategories) && res.albumSubCategories.length > 0) {
            result.push(res.albumSubCategories);
          }
          if (Array.isArray(res.albumProducts) && res.albumProducts.length > 0) {
            result.push(res.albumProducts);
          }
          return result;
        }
        return res || [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Get album enabled options
   */
  getAlbumEnabledOptions(coverSubtypeId: number, albumId: number): Observable<any> {
    const params: Record<string, any> = {};
    if (coverSubtypeId) params['coverSubTypeId'] = coverSubtypeId;
    if (albumId) params['albumId'] = albumId;
    return this.api.get<any>('/products/album-enabled-options', { params }).pipe(
      map(res => {
        // REST API returns { coverSubTypes: SubType[], albumProducts: Product[] }
        // Frontend expects array-of-arrays: [coverSubTypes[], albumProducts[]]
        // with sub_type_id / product_id fields (old AMF naming)
        if (res && !Array.isArray(res)) {
          const covers = (res.coverSubTypes || []).map((s: any) => ({ ...s, sub_type_id: s.id }));
          const products = (res.albumProducts || []).map((p: any) => ({ ...p, product_id: p.id }));
          return [covers, products];
        }
        return res;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Get album covers
   */
  getAlbumCovers(coverSubtypeId: number, albumId: number): Observable<any[]> {
    return this.api.get<any>('/products/covers/' + coverSubtypeId + '/' + albumId).pipe(
      map(res => {
        // REST API wraps in { covers: [...] }
        if (res && !Array.isArray(res) && Array.isArray(res.covers)) {
          return res.covers;
        }
        return res || [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Check default cover
   */
  checkDefaultCover(albumId: number, coverId: number): Observable<any> {
    return this.api.get('/products/albums/' + albumId + '/default-cover/' + coverId).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Get products by type
   */
  getProducts(typeId: number, formType: number = 0, fillFinishOptions: number = 0): Observable<any> {
    return this.api.get<any>('/products/type/' + typeId, { params: { form: formType, fill: fillFinishOptions } }).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * Get products by type, filtered by subcategory (subTypeId)
   */
  getProductsBySubType(typeId: number, subTypeId: number, formType: number = 0, fillFinishOptions: number = 0): Observable<any> {
    return this.api.get<any>('/products/type/' + typeId, {
      params: { form: formType, fill: fillFinishOptions, subTypeId }
    }).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * Get store products
   */
  getStoreProducts(typeId: number, formType: number = 0, fillFinishOptions: number = 0): Observable<ProductInfo[]> {
    return this.api.get<ProductInfo[]>('/products/type/' + typeId, { params: { form: 'store', fill: fillFinishOptions } }).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * Get product options (panels like Stamping, UV, etc.)
   *
   * The REST API returns `{ subCategoriesPanel: PanelOption|null, productsPanel: PanelOption|null }`.
   * All callers of this method want the PRODUCTS panel (sub-products like Álbuns Réplica,
   * Álbum de Provas, Mala/Box, etc.). Falling back to `subCategoriesPanel` would leak SubType
   * entities into the panels stack — they get rendered as if they were products and surface
   * as a "phantom panel" (e.g. a "Formato" panel containing format SubTypes when no matching
   * products are linked). If sub-category navigation is ever needed it should go through a
   * separate code path, not this method.
   */
  getOptions(typeId: number, formType: number, productId: number): Observable<any> {
    return this.api.get<any>('/products/' + productId + '/options', { params: { typeId, formType } }).pipe(
      map(res => {
        if (res && !Array.isArray(res) && Object.prototype.hasOwnProperty.call(res, 'productsPanel')) {
          return this.normalizePanelFileFlags(res.productsPanel ?? null);
        }
        return this.normalizePanelFileFlags(res);
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Resilient variant of {@link getOptions}. `getOptions` collapses every
   * failure into an indistinguishable `null`, so a transient network/CORS
   * failure looks identical to a legitimately empty panel — which is why an
   * album draft sometimes reopens with sub-product panels silently dropped.
   *
   * This surfaces success vs failure (`ok`) and applies a bounded retry ONLY
   * for `status === 0` (network / CORS preflight failures); 4xx/5xx are NOT
   * retried (no point hammering the server on a real "not found"/auth error).
   * Retries carry a `_cors` cache-buster so a poisoned CORS-preflight cache
   * entry is bypassed on a fresh URL (the underscore prefix is load-bearing —
   * the bucket/WAF 403s `?cors=*` but serves `?_cors=*`; see commit f56cd24).
   */
  getOptionsResult(typeId: number, formType: number, productId: number): Observable<OptionsLoadResult> {
    const retryDelaysMs = [250, 600];
    const attempt = (n: number): Observable<OptionsLoadResult> => {
      const params: Record<string, string | number | boolean> = { typeId, formType };
      if (n > 0) {
        params['_cors'] = Date.now();
      }
      return this.api.get<any>('/products/' + productId + '/options', { params }).pipe(
        map(res => {
          const panel = (res && !Array.isArray(res) && Object.prototype.hasOwnProperty.call(res, 'productsPanel'))
            ? this.normalizePanelFileFlags(res.productsPanel ?? null)
            : this.normalizePanelFileFlags(res);
          return { ok: true, panel } as OptionsLoadResult;
        }),
        catchError(err => {
          if (err?.status === 0 && n < retryDelaysMs.length) {
            return timer(retryDelaysMs[n]).pipe(switchMap(() => attempt(n + 1)));
          }
          return of({ ok: false, panel: null, error: err } as OptionsLoadResult);
        })
      );
    };
    return attempt(0);
  }

  /**
   * The REST API serialises product flags like `allowFiles` / `allowFilesQty`
   * as numbers (0 / 1), but the app treats them as booleans. Left raw,
   * `allowFiles: 0` slips past the `allowFiles !== false` panel checks
   * (`0 !== false` is `true`), so the direct "add files" button shows for
   * editor-only products (e.g. the transparent sheet). Coerce defined values
   * to real booleans here — the single options-loading point — and leave
   * undefined/null untouched so the "flag omitted = allowed by default"
   * behaviour is preserved.
   */
  private normalizePanelFileFlags(panel: any): any {
    const options = panel?.options;
    if (Array.isArray(options)) {
      for (const opt of options) {
        this.coerceFileFlag(opt, 'allowFiles');
        this.coerceFileFlag(opt, 'allowFilesQty');
      }
    }
    return panel;
  }

  private coerceFileFlag(obj: any, key: string): void {
    const v = obj?.[key];
    if (v === undefined || v === null || typeof v === 'boolean') {
      return;
    }
    obj[key] = v === 1 || v === '1' || v === 'true';
  }

  /**
   * Get finish options
   */
  getFinishOptions(productId: number, formType: number = 0): Observable<any> {
    return this.api.get('/products/' + productId + '/finishes', { params: { formType } }).pipe(
      tap(response => {
        console.log('[ProductsService] getFinishOptions RAW response for productId', productId, ':', response);
        if (Array.isArray(response) && response.length > 0 && Array.isArray(response[0])) {
          response[0].forEach((opt: any) => {
            console.log('[ProductsService] FinishOption:', opt.name, 'finishes:', opt.finishes?.map((f: any) => f.name));
          });
        }
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Get finish options with smart caching
   * Checks AppStateService cache first, only calls API if not cached
   * AMF: ProductsService.getProductFinishesOptions(productId, formType, locale, user)
   */
  getFinishOptionsWithCache(productId: number, formType: number = 0): Observable<{ options: any[], rules: any[] }> {
    // Check if already cached
    if (this.appState.hasFinishOptions(productId)) {
      const loadingState = this.appState.getFinishOptionsLoadingState(productId);

      if (loadingState === 'loaded') {
        const cachedOptions = this.appState.getFinishOptions(productId) || [];
        const cachedRules = this.appState.getFinishRules(productId) || [];
        console.log(`[ProductsService] Using cached finish options for product ${productId}: ${cachedOptions.length} options, ${cachedRules.length} rules`);
        return of({ options: cachedOptions, rules: cachedRules });
      }

      if (loadingState === 'loading') {
        // Already loading, wait and retry
        console.log(`[ProductsService] Finish options already loading for product ${productId}, waiting...`);
        return new Observable(observer => {
          const checkInterval = setInterval(() => {
            const state = this.appState.getFinishOptionsLoadingState(productId);
            if (state === 'loaded') {
              clearInterval(checkInterval);
              const cachedOptions = this.appState.getFinishOptions(productId) || [];
              const cachedRules = this.appState.getFinishRules(productId) || [];
              observer.next({ options: cachedOptions, rules: cachedRules });
              observer.complete();
            } else if (state === 'error') {
              clearInterval(checkInterval);
              observer.error(new Error('Failed to load finish options'));
            }
          }, 100);

          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkInterval);
            observer.error(new Error('Timeout waiting for finish options'));
          }, 10000);
        });
      }
    }

    // Not cached, load from API
    console.log(`[ProductsService] Loading finish options from API for product ${productId}`);
    this.appState.setFinishOptionsLoading(productId);

    return this.api.get('/products/' + productId + '/finishes', { params: { formType } }).pipe(
      tap(response => {
        console.log('[ProductsService] getFinishOptionsWithCache RAW response for productId', productId, ':', response);

        // Parse response - REST API returns { finishOptions: [...], finishRules: [...] }
        // Legacy AMF returned [[FinishOptionInfo, ...], [FinishRuleInfo, ...]]
        let options: any[] = [];
        let rules: any[] = [];
        const res = response as any;

        if (res?.finishOptions) {
          // New REST API format
          options = res.finishOptions || [];
          rules = res.finishRules || [];
        } else if (Array.isArray(response)) {
          // Legacy AMF format
          if (response.length > 0 && Array.isArray(response[0])) {
            options = response[0];
          } else {
            options = response;
          }

          if (response.length > 1 && Array.isArray(response[1])) {
            rules = response[1];
          }
        }

        console.log(`[ProductsService] Parsed ${options.length} options and ${rules.length} rules for product ${productId}`);
        // Log each option to debug missing type 9
        options.forEach((opt: any, idx: number) => {
          console.log(`[ProductsService] Option ${idx}: id=${opt.id}, name="${opt.name}", type=${opt.type}, finishes=${opt.finishes?.length || 0}`);
        });

        // Cache the results in AppStateService
        this.appState.cacheFinishOptions(productId, options, rules);
      }),
      switchMap(() => {
        // Return cached data after storing
        const cachedOptions = this.appState.getFinishOptions(productId) || [];
        const cachedRules = this.appState.getFinishRules(productId) || [];
        return of({ options: cachedOptions, rules: cachedRules });
      }),
      catchError(error => {
        console.error('[ProductsService] Error loading finish options for product', productId, ':', error);
        this.appState.setFinishOptionsError(productId);
        return of({ options: [], rules: [] });
      })
    );
  }

  /**
   * Get unique finishes
   */
  getUniqueFinishes(typeId: number, finishOptionType: number): Observable<any[]> {
    return this.api.get<any>('/products/' + typeId + '/finishes', { params: { unique: 1, finishOptionType } }).pipe(
      map(res => {
        // REST API wraps in { items: [...] } when unique=1
        if (res && !Array.isArray(res) && Array.isArray(res.items)) return res.items;
        return res || [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Get products with same finishes
   */
  getSameFinishesProducts(typeId: number, finishes: any[]): Observable<any[]> {
    return this.api.post<any>('/products/' + typeId + '/same-finishes', { finishes }).pipe(
      map(res => {
        // REST API wraps in { items: [...] }
        if (res && !Array.isArray(res) && Array.isArray(res.items)) return res.items;
        return res || [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Get product price
   * Strips extra fields before sending — Symfony FinishOptionType only accepts optionId, finishId, value
   */
  getProductPrice(orderItemInfo: any): Observable<any> {
    // Map finishes to only the fields the Symfony form accepts
    const cleanFinishes = (orderItemInfo.finishes || []).map((f: any) => ({
      optionId: f.optionId ?? 0,
      finishId: f.id ?? null,   // ?? preserves 0 as valid finishId
      value: f.data ?? null,    // frontend uses 'data' for freeform values (color, text, dimensions)
    }));

    const payload: any = {
      productId: orderItemInfo.productId,
      qty: orderItemInfo.qty,
      numExtraPages: orderItemInfo.numExtraPages,
      width: orderItemInfo.width,
      height: orderItemInfo.height,
      numPages: orderItemInfo.numPages,
      type: orderItemInfo.type,
      name: orderItemInfo.name,
      productSubTypeId: orderItemInfo.productSubTypeId,
      code: orderItemInfo.code,
      finishes: cleanFinishes,
    };

    return this.api.post('/products/' + orderItemInfo.productId + '/price', payload).pipe(
      catchError(() => of(null))
    );
  }

  /**
   * Group pack pricing — the REST home of legacy AMF getProductPack (price
   * every product, apply pack pricing when the type participates in a pack)
   * is POST /products/pack-price, which returns the repriced GROUP (items
   * serialized as `products`). The similarly-named /products/{id}/pack is a
   * different thing: it returns only the pack DEFINITION ({id, ref, rules})
   * for notification UX — callers that replace their order group with the
   * result must never hit it, or the group loses all products and
   * add-to-cart 400s ("Products are required"), which is exactly what
   * happened for pack-participating types like boxes.
   *
   * Returns null on any failure so callers keep their locally-built group.
   */
  getProductPack(orderItemsGroup: any): Observable<any> {
    const products = Array.isArray(orderItemsGroup?.products) ? orderItemsGroup.products : [];
    if (products.length === 0) return of(null);

    // FinishOptionType is the one strict sub-form (no allow_extra_fields) —
    // map finishes to {optionId, finishId, value} exactly like the
    // add-to-cart payload cleaning does. Everything else passes as-is.
    const payload = {
      ...orderItemsGroup,
      products: products.map((p: any) => ({
        ...p,
        finishes: Array.isArray(p.finishes)
          ? p.finishes.map((f: any) => ({
              optionId: f.optionId ?? 0,
              finishId: f.id ?? null,
              value: f.data ?? null,
            }))
          : p.finishes,
      })),
    };

    return this.api.post('/products/pack-price', payload).pipe(
      map((res: any) => {
        // Don't let the response token block ride along into the group that
        // gets embedded in the next add-to-cart request.
        if (res && typeof res === 'object') delete res._token;
        return res;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Search products
   * AMF: ProductsService.search(text, brand, locale, user)
   */
  search(text: string, brand: string): void {
    if (!text || text.trim().length < 3) {
      this.appState.setSearchResults([]);
      return;
    }
    this.searchSubject.next({ text: text.trim(), brand });
  }

  private performSearch(text: string, brand: string): Observable<ProductInfo[]> {
    return this.api.get<any>('/products/search', { params: { q: text, brand } }).pipe(
      map(res => {
        // REST API wraps in { items: [...], pagination: {...} }
        if (res && !Array.isArray(res) && Array.isArray(res.items)) return res.items;
        return res || [];
      }),
      catchError(() => of([]))
    );
  }

  getSubCategories(categoryId: number): Observable<SubCategory[]> {
    // This is derived from getProducts, not a direct AMF call
    return of([]);
  }

  getProductDetails(productId: number): Observable<ProductInfo | null> {
    // Use getOptions or getFinishOptions depending on need
    return of(null);
  }

  // Category navigation helpers - using ProductType enum as typeId
  getAlbums(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.ALBUM);
  }

  getBoxes(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.BOXES);
  }

  getParents(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.PARENTS);
  }

  getProofs(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.PROOFS);
  }

  getDVDs(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.DVDS);
  }

  getGuest(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.GUEST);
  }

  getStudioBox(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.STUDIOBOX);
  }

  getBookBox(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.BOOKBOX);
  }

  getSignatures(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.SIGNATURES);
  }

  getShowcases(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.SHOWCASES);
  }

  getShowcaseCovers(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.SHOWCASE_COVERS);
  }

  getDecor(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.DECOR);
  }

  getGifts(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.GIFT);
  }

  getCalendars(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.CALENDAR);
  }

  getRigids(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.RIGIDS);
  }

  getFlexibles(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.FLEXIBLES);
  }

  getPhotosPosters(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.PHOTOS_POSTER);
  }

  getGreetings(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.GREETINGS);
  }

  getOffset(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.OFFSET);
  }

  getCards(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.CARDS);
  }

  getFrames(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.FRAMES);
  }

  getFilmRoll(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.FILMROLL);
  }

  getAnalogAlbums(): Observable<ProductInfo[]> {
    return this.getProducts(ProductType.ANALOG_ALBUNS);
  }
}
